// Path: src/components/RealTimeImageCard.tsx
import React, { useState, useEffect } from 'react'
import { Card } from 'react-bootstrap'
import { RealTimeInferenceResult } from '../types/prediction'
import { PredictionMetadata } from '../types/models/prediction'
import { useAppSelector } from '../store/hooks'
import { selectSelectedProjectId } from '../store/slices/projectSlice'
import { useGetProjectQuery } from '../services/projectsApi'
import { AnnotationDataBoundingBox } from '../types/models/annotation'
import ImageCard from './annotation/ContentImageCard'
import styled from 'styled-components'

interface RealTimeImageProps {
  result: RealTimeInferenceResult
  imageData: {
    url: string
    width: number
    height: number
  }
  cardSize?: number
  showConfidence?: boolean
}

const StyledCard = styled(Card)<{ $cardSize: number }>`
  width: ${(props) => props.$cardSize}px;
  cursor: pointer;
  position: relative;
  background-color: #2a2a2a;
  border: 1px solid #404040;
  transition: transform 0.2s ease-in-out;
`

const NoPredictionsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 16px;
  z-index: 20;
  text-align: center;
  padding: 20px;
`

const RealTimeImage: React.FC<RealTimeImageProps> = ({
  result,
  imageData,
  cardSize = 512,
  showConfidence = true,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const [imageBoundingBoxes, setImageBoundingBoxes] = useState<AnnotationDataBoundingBox[]>([])

  useEffect(() => {
    const bboxes = result.predictions
      .map((predictionMeta: PredictionMetadata) => {
        const bboxData = predictionMeta.bounding_boxes || predictionMeta.oriented_bounding_boxes

        if (!bboxData) return null

        // For real-time inference, coordinates are typically already in pixel format
        // but we still need to handle both cases for consistency
        let bbox: AnnotationDataBoundingBox

        if (bboxData.points && bboxData.points.length === 8) {
          // Case 1: Points exist (check if they need coordinate transformation)
          const maxX = Math.max(
            bboxData.points[0],
            bboxData.points[2],
            bboxData.points[4],
            bboxData.points[6]
          )
          const maxY = Math.max(
            bboxData.points[1],
            bboxData.points[3],
            bboxData.points[5],
            bboxData.points[7]
          )

          let finalPoints: number[]
          let xmin: number, ymin: number, xmax: number, ymax: number

          if (maxX <= 1 && maxY <= 1 && imageData.width && imageData.height) {
            // Points are normalized (0-1), convert to pixel coordinates
            finalPoints = [
              bboxData.points[0] * imageData.width, // x1
              bboxData.points[1] * imageData.height, // y1
              bboxData.points[2] * imageData.width, // x2
              bboxData.points[3] * imageData.height, // y2
              bboxData.points[4] * imageData.width, // x3
              bboxData.points[5] * imageData.height, // y3
              bboxData.points[6] * imageData.width, // x4
              bboxData.points[7] * imageData.height, // y4
            ]

            // Calculate min/max from pixel coordinates
            const xValues = [finalPoints[0], finalPoints[2], finalPoints[4], finalPoints[6]]
            const yValues = [finalPoints[1], finalPoints[3], finalPoints[5], finalPoints[7]]
            xmin = Math.min(...xValues)
            ymin = Math.min(...yValues)
            xmax = Math.max(...xValues)
            ymax = Math.max(...yValues)
          } else {
            // Points are already in pixel coordinates
            finalPoints = [...bboxData.points]
            const xValues = [finalPoints[0], finalPoints[2], finalPoints[4], finalPoints[6]]
            const yValues = [finalPoints[1], finalPoints[3], finalPoints[5], finalPoints[7]]
            xmin = Math.min(...xValues)
            ymin = Math.min(...yValues)
            xmax = Math.max(...xValues)
            ymax = Math.max(...yValues)
          }

          bbox = {
            class_name: predictionMeta.class_name,
            points: finalPoints,
            xmin,
            ymin,
            xmax,
            ymax,
            name: predictionMeta.tagid,
            confidence: predictionMeta.confidence,
            class_index: predictionMeta.class_index,
          } as AnnotationDataBoundingBox
        } else if (
          bboxData.xmin !== undefined &&
          bboxData.xmax !== undefined &&
          bboxData.ymin !== undefined &&
          bboxData.ymax !== undefined
        ) {
          // Case 2: Only min/max exists
          let xmin: number, ymin: number, xmax: number, ymax: number

          if (bboxData.xmax <= 1 && bboxData.ymax <= 1 && imageData.width && imageData.height) {
            // Coordinates are normalized (0-1), convert to pixel coordinates
            xmin = bboxData.xmin * imageData.width
            ymin = bboxData.ymin * imageData.height
            xmax = bboxData.xmax * imageData.width
            ymax = bboxData.ymax * imageData.height
          } else {
            // Coordinates are already in pixel format
            xmin = bboxData.xmin
            ymin = bboxData.ymin
            xmax = bboxData.xmax
            ymax = bboxData.ymax
          }

          // Create points array from min/max (in pixel coordinates)
          const points = [
            xmin,
            ymin, // top-left
            xmax,
            ymin, // top-right
            xmax,
            ymax, // bottom-right
            xmin,
            ymax, // bottom-left
          ]

          bbox = {
            class_name: predictionMeta.class_name,
            points,
            xmin,
            ymin,
            xmax,
            ymax,
            name: predictionMeta.tagid,
            confidence: predictionMeta.confidence,
            class_index: predictionMeta.class_index,
          } as AnnotationDataBoundingBox
        } else {
          return null
        }

        return bbox
      })
      .filter((box): box is AnnotationDataBoundingBox => box !== null)

    setImageBoundingBoxes(bboxes)
  }, [result, imageData])

  const hasNoPredictions = !result.predictions || result.predictions.length === 0

  const getNoPredictionsMessage = () => {
    if (!project) return 'No predictions available'

    switch (project.annotation_type) {
      case 'classification':
        return 'No classes were detected in this image'
      case 'bounding_box':
        return 'No objects were detected in this image'
      case 'oriented_bounding_box':
        return 'No objects were detected in this image'
      default:
        return 'No predictions available'
    }
  }

  return (
    <StyledCard $cardSize={cardSize}>
      <div
        style={{
          width: cardSize,
          height: cardSize,
          backgroundColor: '#1a1a1a',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <ImageCard
          base64Data={imageData.url}
          bounding_boxes={imageBoundingBoxes}
          contentWidth={imageData.width}
          contentHeight={imageData.height}
          showConfidence={showConfidence}
        />
        {hasNoPredictions && (
          <NoPredictionsOverlay>{getNoPredictionsMessage()}</NoPredictionsOverlay>
        )}
      </div>
    </StyledCard>
  )
}

export default RealTimeImage
